import { addMonths, format, lastDayOfMonth } from 'date-fns'

export type MetropolitanItemType = 'PARCELA' | 'BALAO'
export type MetropolitanItemStatus = 'PREVISTO' | 'PREPARADO' | 'EMITIDO' | 'PAGO' | 'VENCIDO' | 'CANCELADO' | 'ERRO'


const SCALE = 1_000_000_000_000n
const MONEY_SCALE = 100n
/** Converts Brazilian or international decimal input to PostgreSQL's dot notation. */
export function databaseDecimal(value: string | number) {
  const source = String(value ?? '').trim().replace(/\s/g, '')
  const comma = source.lastIndexOf(','), dot = source.lastIndexOf('.')
  const decimalSeparator = comma > dot ? ',' : dot >= 0 ? '.' : ''
  if (!decimalSeparator) return source.replace(/\D/g, '')
  const position = source.lastIndexOf(decimalSeparator)
  return `${source.slice(0, position).replace(/\D/g, '')}.${source.slice(position + 1).replace(/\D/g, '')}`
}
/** Renders a decimal value with a Brazilian comma, without changing its precision. */
export function brazilianDecimal(value: string | number) {
  return databaseDecimal(value).replace('.', ',')
}
/** An optional one-off boleto amount, normalized to cents without CUB arithmetic. */
export function exceptionalBoletoAmount(value: string) {
  const input = value.trim()
  if (!/^(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d{1,2})?$|^\d+\.\d{1,2}$/.test(input)) {
    throw new Error('Informe o valor excepcional em reais, com até duas casas decimais.')
  }
  const normalized = databaseDecimal(input)
  const [whole, cents = ''] = normalized.split('.')
  if (Number(normalized) <= 0) throw new Error('O valor excepcional deve ser maior que zero.')
  return `${whole}.${cents.padEnd(2, '0')}`
}
function decimal(value: string) {
  const [whole = '0', fraction = ''] = databaseDecimal(value).replace(/^\+/, '').split('.')
  const sign = whole.startsWith('-') ? -1n : 1n
  return sign * (BigInt(whole.replace('-', '') || '0') * SCALE + BigInt((fraction + '000000000000').slice(0, 12)))
}
function roundedDivision(numerator: bigint, denominator: bigint) { return (numerator + denominator / 2n) / denominator }
function render(raw: bigint, scale: bigint, places: number) {
  const sign = raw < 0n ? '-' : ''; const abs = raw < 0n ? -raw : raw
  const whole = abs / scale; const fraction = (abs % scale).toString().padStart(places, '0')
  return `${sign}${whole}.${fraction}`
}

/** Fixed CUB quantity, rounded to 12 decimal places, without IEEE-754 arithmetic. */
export function cubQuantity(baseAmount: string, referenceCub: string) {
  const ref = decimal(referenceCub); if (ref <= 0n) throw new Error('CUB de referência inválido')
  return render(roundedDivision(decimal(baseAmount) * SCALE, ref), SCALE, 12)
}
/** Final monetary value: fixed CUB quantity × CUB in effect, rounded only to cents. */
export function cubUpdatedAmount(quantity: string, currentCub: string) {
  const cents = roundedDivision(decimal(quantity) * decimal(currentCub), SCALE * (SCALE / MONEY_SCALE))
  return render(cents, MONEY_SCALE, 2)
}

export function digits(value: string) { return value.replace(/\D/g, '') }
export function compareMetropolitanUnitCodes(left: string, right: string) {
  return left.localeCompare(right, 'pt-BR', { numeric: true, sensitivity: 'base' })
}
export function validCpfCnpj(value: string) {
  const doc = digits(value); if (!/^\d{11}$|^\d{14}$/.test(doc) || /^([0-9])\1+$/.test(doc)) return false
  const check = (numbers: string, weights: number[]) => {
    const sum = weights.reduce((total, weight, index) => total + Number(numbers[index]) * weight, 0)
    const result = sum % 11 < 2 ? 0 : 11 - (sum % 11); return result === Number(numbers.at(-1))
  }
  return doc.length === 11
    ? check(doc.slice(0, 10), [10,9,8,7,6,5,4,3,2]) && check(doc, [11,10,9,8,7,6,5,4,3,2])
    : check(doc.slice(0, 13), [5,4,3,2,9,8,7,6,5,4,3,2]) && check(doc, [6,5,4,3,2,9,8,7,6,5,4,3,2])
}

/** Bank-visible charge labels. Internal idempotency remains a separate key. */
export function metropolitanChargeLabels(unitCode: string, itemType: MetropolitanItemType, itemNumber: number, itemTotal: number) {
  const controlCode = unitCode.trim()
  if (!controlCode) throw new Error('Código da unidade obrigatório')
  if (controlCode.length > 15) throw new Error('Código da unidade excede o limite de 15 caracteres do Banco Inter')
  const itemLabel = itemType === 'BALAO' ? 'Balão' : 'Parcela'
  return { controlCode, description: `Cota ${controlCode} - ${itemLabel} ${itemNumber} de ${itemTotal}` }
}

export function monthlyItems(firstDueDate: string, dueDay: number, count: number, baseAmount: string, referenceCub: string, unitCode: string) {
  return Array.from({ length: count }, (_, index) => {
    const month = addMonths(new Date(`${firstDueDate}T12:00:00`), index)
    const due = new Date(month.getFullYear(), month.getMonth(), Math.min(dueDay, lastDayOfMonth(month).getDate()))
    return { competence: format(due, 'yyyy-MM-01'), item_type: 'PARCELA', item_number: index + 1, item_total: count,
      display_fraction: `${index + 1}/${count}`, base_amount: baseAmount, cub_quantity: cubQuantity(baseAmount, referenceCub), due_date: format(due, 'yyyy-MM-dd'), description: metropolitanChargeLabels(unitCode, 'PARCELA', index + 1, count).description }
  })
}
